import { isIOSDevice } from '@theme/utilities';

/**
 * BackToTop Component
 * A button that smoothly scrolls the page to the top when clicked
 */
class BackToTop extends HTMLElement {
  constructor() {
    super();
    this.button = this.querySelector('button');
    this.#boundScrollToTop = this.scrollToTop.bind(this);
  }

  /** @type {() => void} */
  #boundScrollToTop;

  connectedCallback() {
    if (this.button) {
      this.button.addEventListener('click', this.#boundScrollToTop);
    }
  }

  disconnectedCallback() {
    if (this.button) {
      this.button.removeEventListener('click', this.#boundScrollToTop);
    }
  }

  scrollToTop() {
    document.documentElement.setAttribute('data-scrolling-to-top', '');

    // iOS Safari crashes when native smooth scroll holds all decoded image textures for the
    // entire scroll range in GPU compositor memory simultaneously. Animate with repeated
    // instant scrolls instead — the browser only composites the visible viewport each frame,
    // so off-screen images are never all resident at once.
    if (isIOSDevice()) {
      this.#animatedScrollToTop();
      return;
    }

    window.scrollTo({ top: 0, behavior: 'smooth' });

    const clear = () => document.documentElement.removeAttribute('data-scrolling-to-top');

    // scrollend is the cleanest signal; fall back to a debounced scroll event
    if ('onscrollend' in window) {
      window.addEventListener('scrollend', clear, { once: true });
    } else {
      let timer;
      const onScroll = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          window.removeEventListener('scroll', onScroll);
          clear();
        }, 100);
      };
      window.addEventListener('scroll', onScroll, { passive: true });
    }
  }

  /**
   * Animate scroll-to-top on iOS using rAF + instant repositioning.
   * Visually equivalent to smooth scroll but avoids GPU compositor memory pressure.
   */
  #animatedScrollToTop() {
    const start = window.scrollY;
    if (start === 0) {
      document.documentElement.removeAttribute('data-scrolling-to-top');
      return;
    }

    // Release decoded image memory before animating. This is the primary fix for the
    // remaining crash: even with frame-by-frame scrolling, iOS kills the tab if total
    // decoded image memory (accumulated from lazy-loading while scrolling down) is too
    // high when we arrive at the top. Releasing images > 2vh below current viewport
    // frees that memory before the animation runs.
    this.#releaseOffscreenImages();

    const startTime = performance.now();
    const duration = Math.min(start * 0.4, 600);

    const step = (timestamp) => {
      const t = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out cubic
      window.scrollTo({ top: Math.round(start * (1 - eased)), behavior: 'instant' });

      if (t < 1) {
        requestAnimationFrame(step);
      } else {
        document.documentElement.removeAttribute('data-scrolling-to-top');
      }
    };

    requestAnimationFrame(step);
  }

  /**
   * Clear src/srcset from images well below the viewport to free decoded image memory.
   * An IntersectionObserver restores each image before it scrolls back into view.
   */
  #releaseOffscreenImages() {
    // Images loaded while the user scrolled down are ABOVE the current viewport —
    // getBoundingClientRect().top is negative for them. Release those more than 2
    // viewports above so their decoded pixels are freed before the animation runs.
    const buffer = window.innerHeight * 2;

    /** @type {Map<HTMLImageElement, { src: string; srcset: string }>} */
    const released = new Map();

    document.querySelectorAll('img').forEach((rawImg) => {
      const img = /** @type {HTMLImageElement} */ (rawImg);

      // Skip above-fold images and anything not yet loaded (no src = nothing to release)
      if (img.loading === 'eager' || !img.getAttribute('src')) return;

      // Only release images well above the viewport (already scrolled past)
      if (img.getBoundingClientRect().top > -buffer) return;

      released.set(img, {
        src: img.getAttribute('src') ?? '',
        srcset: img.getAttribute('srcset') ?? '',
      });

      img.removeAttribute('src');
      img.removeAttribute('srcset');
    });

    if (!released.size) return;

    // Restore images as they re-enter the viewport when scrolling back down.
    // rootMargin of 1 viewport ensures restoration happens before they're visible.
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const img = /** @type {HTMLImageElement} */ (entry.target);
          const data = released.get(img);
          if (!data) return;
          img.setAttribute('src', data.src);
          if (data.srcset) img.setAttribute('srcset', data.srcset);
          observer.unobserve(img);
          released.delete(img);
        });
      },
      { rootMargin: `${window.innerHeight}px` },
    );

    released.forEach((_, img) => observer.observe(img));
  }
}

customElements.define('back-to-top', BackToTop);
